import { Clock, FastForward, RotateCcw } from "@tamagui/lucide-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Input, Popover, Separator, XStack, YStack } from "tamagui";

import {
  getGetTimeTravelOffsetQueryKey,
  useClearTimeTravelOffset,
  useFastForwardTimeTravel,
  useGetTimeTravelOffset,
} from "@/api/generated";
import { Button } from "@/components/Button";
import ChatIcon from "@/components/ChatIcon";
import { Text } from "@/components/Typography";
import { useAuthState } from "@/hooks/useAuthState";

export default function TimeTravelControl() {
  const { userId } = useAuthState();
  const [isOpen, setIsOpen] = useState(false);
  const [offsetInput, setOffsetInput] = useState("0");
  const queryClient = useQueryClient();

  const { data: state } = useGetTimeTravelOffset({
    query: { enabled: isOpen && !!userId },
  });

  const { mutate: fastForward, isPending: isFastForwarding } =
    useFastForwardTimeTravel();

  const { mutate: clearOffset, isPending: isClearing } =
    useClearTimeTravelOffset();

  const loading = isFastForwarding || isClearing;

  useEffect(() => {
    if (state) {
      setOffsetInput(String(state.advance_days ?? 0));
    }
  }, [state]);

  if (!userId) {
    return null;
  }

  const invalidateState = () =>
    queryClient.invalidateQueries({
      queryKey: getGetTimeTravelOffsetQueryKey(),
    });

  const handleFastForward = () => {
    fastForward(
      { data: { advance_days: parseFloat(offsetInput) || 0 } },
      { onSuccess: invalidateState },
    );
  };

  const handleReset = () => {
    clearOffset(undefined, {
      onSuccess: () => {
        setOffsetInput("0");
        invalidateState();
      },
    });
  };

  const simulatedDate = state?.simulated_now_utc
    ? new Date(state.simulated_now_utc).toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  return (
    <Popover
      allowFlip
      placement="top-start"
      offset={{ mainAxis: 6, crossAxis: -10 }}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Popover.Trigger asChild>
        <ChatIcon
          Icon={Clock}
          ariaLabel="Time travel controls"
          active={isOpen}
          disableWhenActive={false}
          mode={state && state.advance_days > 0 ? "primary" : "tertiary"}
        />
      </Popover.Trigger>
      <Popover.FocusScope>
        <Popover.Content
          borderRadius="$150"
          backgroundColor="$fillInput"
          shadowRadius="$400"
          shadowColor="$shadowColor"
          shadowOffset={{ height: 4 }}
          padding="$150"
          minWidth={260}
        >
          <Popover.Arrow />
          <Popover.Close />
          <YStack gap="$100">
            <Text size="$xs" fontWeight="bold">
              Time Travel
            </Text>

            {state && state.advance_days > 0 && (
              <YStack
                backgroundColor="$backgroundHover"
                borderRadius="$100"
                padding="$75"
              >
                <Text size="$2xs" color="$textSecondary">
                  Simulated: {simulatedDate}
                </Text>
                <Text size="$2xs" color="$textSecondary">
                  Offset: {state.advance_days} day(s)
                </Text>
              </YStack>
            )}

            <Separator borderColor="$backgroundGround" />

            <XStack gap="$75" alignItems="center">
              <Text size="$2xs" width={70}>
                Days:
              </Text>
              <Input
                size="$2"
                value={offsetInput}
                onChangeText={setOffsetInput}
                keyboardType="numeric"
                flex={1}
              />
            </XStack>

            <XStack gap="$75">
              <Button.Wrapper
                flex={1}
                padding="$75"
                alignItems="center"
                justifyContent="center"
                onPress={handleFastForward}
                disabled={loading}
                cursor="pointer"
                borderRadius="$100"
                backgroundColor="$backgroundHover"
                hoverStyle={{ backgroundColor: "$navHover" }}
              >
                <XStack gap="$50" alignItems="center">
                  <FastForward size={14} color="$textSecondary" />
                  <Text size="$2xs">FF</Text>
                </XStack>
              </Button.Wrapper>

              <Button.Wrapper
                flex={1}
                padding="$75"
                alignItems="center"
                justifyContent="center"
                onPress={handleReset}
                disabled={loading}
                cursor="pointer"
                borderRadius="$100"
                backgroundColor="$backgroundHover"
                hoverStyle={{ backgroundColor: "$navHover" }}
              >
                <XStack gap="$50" alignItems="center">
                  <RotateCcw size={14} color="$textSecondary" />
                  <Text size="$2xs">Reset</Text>
                </XStack>
              </Button.Wrapper>
            </XStack>
          </YStack>
        </Popover.Content>
      </Popover.FocusScope>
    </Popover>
  );
}
